import { useContext } from 'react';
import { flexRender, type ColumnDef } from '@/lib/tanstack-table';
import type { AssignmentLine } from 'src/api/rpc/hooks/procurement/useReplenishmentSuggestions';
import {
  NeedRowsContext,
  type LineCell,
  type LineCellContext,
} from './needRowsContext';
import { NeedRowCell } from './NeedRowCell';
import { SoRowCell } from './SoRowCell';

// One need-aware renderer per base renderer, so a rebuild of the columns keeps
// each cell's identity and React doesn't remount every cell.
const needAwareCells = new WeakMap<object, LineCell>();

function needAwareCell(base: LineCell): LineCell {
  const cached = typeof base === 'function' ? needAwareCells.get(base) : null;
  if (cached) return cached;
  const cell: LineCell = (context) => (
    <NeedAwareCell context={context} base={base} />
  );
  if (typeof base === 'function') needAwareCells.set(base, cell);
  return cell;
}

/** A line column whose cells also draw need rows and their SO rows. */
export function withNeedRows(
  column: ColumnDef<AssignmentLine>,
): ColumnDef<AssignmentLine> {
  const base = column.cell;
  if (base == null) return column;
  return { ...column, cell: needAwareCell(base) };
}

function NeedAwareCell({
  context,
  base,
}: {
  context: LineCellContext;
  base: LineCell;
}) {
  const needRows = useContext(NeedRowsContext);
  const id = context.row.original.suggestionId;
  const need = needRows?.needs.get(id);
  if (needRows && need) {
    return (
      <NeedRowCell
        need={need}
        needRows={needRows}
        context={context}
        base={base}
      />
    );
  }
  if (needRows?.needOf.has(id)) {
    return <SoRowCell context={context} base={base} />;
  }
  return flexRender(base, context);
}
